import { createContext, useContext, useState } from 'react'
import { firstVideoMediaIndex, type MediaItem } from './cardMediaUtils'

/**
 * Namespace prefixed to every CardMedia video id. The default ('') keeps the
 * public `${postId}-${index}` ids that surfaces compute externally. Studio
 * previews set it so two concurrent previews of one post never share a
 * videoPlaybackStore entry.
 */
export const VideoIdScopeContext = createContext<string>('')

export function useVideoIdScope(): string {
  return useContext(VideoIdScopeContext)
}

export function scopedVideoId(scope: string, postId: string, index: number): string {
  const base = `${postId}-${index}`
  return scope ? `${scope}:${base}` : base
}

/**
 * The scoped id of the first video `CardMedia` renders for this post, or null
 * when the post has no video: what a self-managed surface presents
 * (`videoPlaybackStore.presentVideo`) for its current card.
 */
export function firstVideoId(
  scope: string,
  post: { id: string; media?: readonly MediaItem[] | null },
): string | null {
  const index = firstVideoMediaIndex(post.media)
  return index < 0 ? null : scopedVideoId(scope, post.id, index)
}

/** True when `videoId` was built by `scopedVideoId` with this (non-empty) scope. */
export function videoIdInScope(scope: string, videoId: string | null | undefined): boolean {
  return Boolean(scope) && typeof videoId === 'string' && videoId.startsWith(`${scope}:`)
}

let nextSurfaceScope = 0

/**
 * A unique, stable scope for one surface instance, nested under any scope the
 * surface was mounted in (Studio previews) so outer ownership checks still
 * match. Provide it via VideoIdScopeContext and build this surface's own ids
 * with scopedVideoId(scope, …).
 *
 * Client-only: the widget never server-renders and Studio is a SPA, so a
 * per-module counter cannot cause a hydration mismatch.
 */
export function useSurfaceVideoScope(prefix: string): string {
  const parent = useVideoIdScope()
  // A module-wide counter, not `useId`: Preact's `useId` is only unique within
  // one render root (two embedded widgets on a page both get `P0-0`), and
  // React's ids contain `:`, the separator `scopedVideoId` and `videoIdInScope`
  // rely on. Captured once per mount, so it is stable across renders.
  const [own] = useState(() => `${prefix}-${++nextSurfaceScope}`)
  return parent ? `${parent}:${own}` : own
}
